import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faMapMarkerAlt, faBullseye, faSearch, faChevronDown, faTimes, faClipboardList, faHeart, faCog, faEnvelope, faSignOutAlt, faUser, faFilter, faGlobe, faLocation, faTrain, faUniversity, faLandmark, faSchool, faSliders, faBarChart, faRefresh } from '@fortawesome/free-solid-svg-icons';
import './MobileHeader.css';
import { cities, regions } from '../constants/locations';
import MapModal from './MapModal';
import { fetchPlaceSuggestions } from './api';
import LoginModal from './LoginModal';
import LogoutConfirmModal from './LogoutConfirmModal';
import SettingsModal from './SettingsModal';
import ContactUsModal from './ContactUsModal';
import ForgotPasswordModal from './ForgotPasswordModal';
import RegisterModal from './RegisterModal';
import FeatureModal from './FeatureModal';
import PostTypeModal from './PostTypeModal';
import cityIcon from '../assets/city.png';
import regionIcon from '../assets/region.png';
import bedIcon from '../assets/bed.png';
import teamupIcon from '../assets/teamup.png';
import allIcon from '../assets/all.png';


// ─── Hero Banner (merged from HeroBanner.js) ────────────────────────────────
function HeroBanner({
  currentLanguage,
  getLocalizedText,
  activeFilter,    // استقبال حالة الفلتر الحالية من الأب
  onFilterChange   // استقبال وظيفة التغيير من الأب
}) {
  const t = (ar, en) => getLocalizedText(ar, en);

  // استخدام القيمة القادمة من الأب كقيمة افتراضية للحالة المحلية
  const [localActiveFilter, setLocalActiveFilter] = useState(activeFilter || 'all');

  // تحديث الحالة المحلية فور تغير الـ Prop القادم من الأب
  useEffect(() => {
    if (activeFilter) {
      setLocalActiveFilter(activeFilter);
    }
  }, [activeFilter]);

  const handleFilterClick = (type) => {
    setLocalActiveFilter(type); // تحديث الشكل فوراً (UI)
    if (onFilterChange) {
      onFilterChange(type); // إبلاغ المكون الأب بالتغيير ليقوم بإخفاء/إظهار الـ Sidebar
    }
  };

  return (
    <div className="hero-banner">
          <div className="hero-buttons">
            <button 
              className={`hero-btn all-btn ${localActiveFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleFilterClick('all')}
            >
              <img src={allIcon} alt="" className="hero-btn-icon-all" />
              {t("الكل", "All")}
            </button>
            
            <button 
              className={`hero-btn rooms-btn ${localActiveFilter === 'rooms' ? 'active' : ''}`}
              onClick={() => handleFilterClick('rooms')}
            >
              <img src={bedIcon} alt="" className="hero-btn-icon-room" />
              {t("الغرف", "Rooms")}
            </button>
            
            <button 
              className={`hero-btn teamup-btn ${localActiveFilter === 'teamup' ? 'active' : ''}`}
              onClick={() => handleFilterClick('teamup')}
            >
              <img src={teamupIcon} alt="" className="hero-btn-icon-team" />
              {t("فرق السكن", "Team-Ups")}
            </button>
          </div>
    </div>
  );
}
// ────────────────────────────────────────────────────────────────────────────


function MobileHeader({
  currentLanguage,
  toggleLanguage,
  updateCityAndRegion,
  user,
  userFullName,
  onLogout,
  updateSelectedLocation,
  isHeaderOnly = false,
  activeHeroFilter = 'all',
  onHeroFilterChange,
}) {

  const getLocalizedText = (ar, en) => (currentLanguage === 'ar' ? ar : en);

  // دالة بسيطة لاكتشاف لغة النص اللي المستخدم كاتبه
  // لو فيه حروف عربي تعتبر النص عربي، غير كده انجليزي
  const detectQueryLanguage = (text) => {
    const arabicRegex = /[\u0600-\u06FF]/;
    return arabicRegex.test(text) ? 'ar' : 'en';
  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showContactUsModal, setShowContactUsModal] = useState(false);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [regionHintVisible, setRegionHintVisible] = useState(false);
  const debounceTimeout = useRef(null);
  const regionHintTimeout = useRef(null);

  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [featureType, setFeatureType] = useState(null); // 'metro' | 'university' | 'square'

  const [showPostTypeModal, setShowPostTypeModal] = useState(false);

  const drawerRef = useRef(null);
  const searchBarRef = useRef(null);
  const cityDropdownRef = useRef(null);
  const regionDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setIsDrawerOpen(false);
      }
      if (searchBarRef.current && !searchBarRef.current.contains(e.target)) {
        setIsSearchDropdownOpen(false);
      }
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(e.target)) {
        setIsCityDropdownOpen(false);
      }
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(e.target)) {
        setIsRegionDropdownOpen(false);
      }
    };
    if (isDrawerOpen || isSearchDropdownOpen || isCityDropdownOpen || isRegionDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDrawerOpen, isSearchDropdownOpen, isCityDropdownOpen, isRegionDropdownOpen]);

  // location dropdowns no longer fetch per-city/region ad counts (saves Firestore reads)

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setSelectedRegion(null);
    setIsCityDropdownOpen(false);
    setRegionHintVisible(false);
    if (regionHintTimeout.current) clearTimeout(regionHintTimeout.current);
    updateCityAndRegion(city.value, '');
  };

  const handleRegionSelect = (region) => {
    setSelectedRegion(region);
    setIsRegionDropdownOpen(false);
    updateCityAndRegion(selectedCity?.value || '', region.value);
  };

  const handleRegionClick = () => {
    if (selectedCity) {
      setRegionHintVisible(false);
      if (regionHintTimeout.current) clearTimeout(regionHintTimeout.current);
      setIsRegionDropdownOpen(!isRegionDropdownOpen);
      return;
    }
    setIsRegionDropdownOpen(false);
    setRegionHintVisible(true);
    if (regionHintTimeout.current) clearTimeout(regionHintTimeout.current);
    regionHintTimeout.current = setTimeout(() => setRegionHintVisible(false), 2500);
  };

  const handleSearchInputChange = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setErrorMessage('');

    if (query.length === 0) {
      updateSelectedLocation(null);
    }

    if (query.length < 4) {
      setSuggestions([]);
      return;
    }

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      try {
        const queryLang = detectQueryLanguage(query);
        const results = await fetchPlaceSuggestions(query, selectedCity ? selectedCity.value : null, queryLang);
        setSuggestions(results);
      } catch (error) {
        setErrorMessage(error.message);
        setSuggestions([]);
      }
    }, 500);
  };

  const handleSuggestionSelect = (suggestion) => {
    setSearchQuery(suggestion.properties.formatted);
    setIsSearchDropdownOpen(false);
    updateSelectedLocation({
      lat: suggestion.properties.lat,
      lng: suggestion.properties.lon
    });
  };

  const handleSearchOptionSelect = (e) => {
    e.stopPropagation();
    setIsSearchDropdownOpen(false);
    setShowMapModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    onLogout();
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleSearchReset = () => {
    setSelectedCity(null);
    setSelectedRegion(null);
    setSearchQuery('');
    setSuggestions([]);
    setIsCityDropdownOpen(false);
    setIsRegionDropdownOpen(false);
    setIsSearchDropdownOpen(false);
    updateCityAndRegion('', '');
    if (updateSelectedLocation) updateSelectedLocation(null);
  };

  return (
    <>
      <header className="mobile-header">
        {/* Row 1: Logo + Hero Banner + Hamburger */}
        <div className="mobile-row top-row">
          <Link to="/" className="mobile-logo">
            <picture>
              <source media="(max-width: 950px)" srcSet="/assets/croplogo.png" />
              <img src="/assets/logo.png" alt="ROOMATE" />
            </picture>
          </Link>

          {/* Hero buttons inline with logo/hamburger below 950px */}
          <div className="hero-in-top">
            {!isHeaderOnly && (
              <HeroBanner
                currentLanguage={currentLanguage}
                getLocalizedText={getLocalizedText}
                activeFilter={activeHeroFilter}
                onFilterChange={onHeroFilterChange}
              />
            )}
          </div>

          <button className="hamburger-btn" onClick={() => setIsDrawerOpen(true)}>
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>

        {/* Hero buttons in own row at 950px and up */}
        <div className="mobile-row hero-row hero-below">
           {!isHeaderOnly && (
            <HeroBanner
              currentLanguage={currentLanguage}
              getLocalizedText={getLocalizedText}
              activeFilter={activeHeroFilter}
              onFilterChange={onHeroFilterChange}
            />
          )}
        </div>

        
        {/* Row 2+3: Unified Map / City / Region bar */}
        <div className="mobile-row location-bar-row" ref={searchBarRef}>
          <div className="location-bar">

            {/* Map segment - opens dropdown with Set on Map / Metro / Universities / Squares */}


            {/* City segment - same as existing city selector */}
            <div
              ref={cityDropdownRef}
              className={`location-bar-item city-item ${selectedCity ? 'selected' : ''}`}
              onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)}
            >
              <span className="location-bar-label">{getLocalizedText('المدينة', 'City')}</span>
              <span className="location-bar-sub">
                {selectedCity ? getLocalizedText(selectedCity.ar, selectedCity.en) : getLocalizedText('اختر المدينة', 'Select City')}
              </span>

              {isCityDropdownOpen && (
                <div className="dropdown-menu city-dropdown">
                  {cities.map((city, i) => (
                    <div key={i} className="dropdown-item" onClick={() => handleCitySelect(city)}>
                      {getLocalizedText(city.ar, city.en)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <span className="location-bar-divider" />

            {/* Region segment - same as existing region selector */}
            <div
              ref={regionDropdownRef}
              className={`location-bar-item region-item ${selectedRegion ? 'selected' : ''}`}
              onClick={handleRegionClick}
            >
              <span className="location-bar-label">{getLocalizedText('المنطقة', 'Region')}</span>
              <span className="location-bar-sub">
                {selectedRegion ? getLocalizedText(selectedRegion.ar, selectedRegion.en) : getLocalizedText('اختر المنطقة', 'Select Region')}
              </span>

              {regionHintVisible && !selectedCity && (
                <div className="region-select-hint" role="status">
                  {getLocalizedText('اختر المدينة أولاً', 'Select a city first')}
                </div>
              )}

              {isRegionDropdownOpen && selectedCity && (
                <div className="dropdown-menu region-dropdown">
                  {regions[selectedCity.value] && regions[selectedCity.value].length > 0 ? (
                    regions[selectedCity.value].map((region, i) => (
                      <div key={i} className="dropdown-item" onClick={() => handleRegionSelect(region)}>
                        {getLocalizedText(region.ar, region.en)}
                      </div>
                    ))
                  ) : (
                    <div className="dropdown-item">لا توجد مناطق</div>
                  )}
                </div>
              )}
            </div>

            <span className="location-bar-divider" />
            
            <div
              className="location-bar-item map-item"
              onClick={() => setIsSearchDropdownOpen(!isSearchDropdownOpen)}
            >
              <span className="location-bar-label">{getLocalizedText('الخريطة', 'Map')}</span>
              <span className="location-bar-sub">
                {searchQuery ? searchQuery : getLocalizedText('تحديد على الخريطة', 'Select on map')}
              </span>

              {isSearchDropdownOpen && (
                <div className="mobile-search-dropdown-menu">
                  {/* 1. تحديد الموقع على الخريطة */}
                  <div
                    className="search-dropdown-item set-location-item"
                    onClick={handleSearchOptionSelect}
                  >
                    <FontAwesomeIcon icon={faLocation} style={{ fontSize: '1.1em', marginInlineEnd: '6px', color: '#555' }} />
                    {getLocalizedText("تحديد الموقع على الخريطة", "Set Location on Map")}
                  </div>

                  {/* 2. محطات المترو */}
                  <div
                    className="search-dropdown-item"
                    onClick={() => {
                      setIsSearchDropdownOpen(false);
                      setFeatureType('metro');
                      setShowFeatureModal(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faTrain} style={{ fontSize: '1.1em', marginInlineEnd: '6px', color: '#555' }} />
                    {getLocalizedText("محطات مترو", "Metro Stations")}
                  </div>

                  {/* 3. الجامعات */}
                  <div
                    className="search-dropdown-item"
                    onClick={() => {
                      setIsSearchDropdownOpen(false);
                      setFeatureType('university');
                      setShowFeatureModal(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faSchool} style={{ fontSize: '1.1em', marginInlineEnd: '6px', color: '#555' }} />
                    {getLocalizedText("الجامعات", "Universities")}
                  </div>

                  {/* 4. الميادين */}
                  <div
                    className="search-dropdown-item"
                    onClick={() => {
                      setIsSearchDropdownOpen(false);
                      setFeatureType('square');
                      setShowFeatureModal(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faLandmark} style={{ fontSize: '1.1em', marginInlineEnd: '6px', color: '#555' }} />
                    {getLocalizedText("الميادين", "Squares")}
                  </div>
                </div>
              )}
            </div>

            {/* Search reset button: always visible. Resets city/region/map selection. */}
            <button
              className="search-reset-button"
              type="button"
              onClick={handleSearchReset}
              aria-label={getLocalizedText('إعادة تعيين البحث', 'Reset search')}
            >
              <FontAwesomeIcon icon={faRefresh} />
            </button>
            
          </div>
        </div>



       <div className="mobile-separator" />
     </header>

      {/* Hamburger Menu & Modals */}
      <div className={`mobile-menu-overlay ${isDrawerOpen ? 'open' : ''}`} onClick={() => setIsDrawerOpen(false)}>
        <div className="mobile-menu-content drawer-menu" ref={drawerRef} onClick={(e) => e.stopPropagation()}>
          <div className="menu-header">
            <div className="user-info">
              <div className="avatar">
                {user ? (userFullName?.charAt(0)?.toUpperCase() || 'U') : 'G'}
              </div>
              <span>
                {user ? (userFullName || getLocalizedText('مستخدم', 'User')) : getLocalizedText('ضيف', 'Guest')}
              </span>
            </div>
            <button className="close-menu" onClick={() => setIsDrawerOpen(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="menu-items">
            <div 
              className="menu-item" 
              onClick={() => {
                setIsDrawerOpen(false);
                if (user) {
                  window.location.href = '/my-posts';
                } else {
                  localStorage.setItem('redirectAfterLogin', '/my-posts');
                  setShowLoginModal(true);
                }
              }}
            >
              <FontAwesomeIcon icon={faClipboardList} />
              {getLocalizedText('إعلاناتي', 'My Posts')}
            </div>
            <div 
              className="menu-item" 
              onClick={() => {
                setIsDrawerOpen(false);
                if (user) {
                  window.location.href = '/favourites';
                } else {
                  localStorage.setItem('redirectAfterLogin', '/favourites');
                  setShowLoginModal(true);
                }
              }}
            >
              <FontAwesomeIcon icon={faHeart} />
              {getLocalizedText('المفضلة', 'Favourites')}
            </div>

            <div className="menu-item" onClick={toggleLanguage}>
              <FontAwesomeIcon icon={faGlobe} />
              {getLocalizedText('English', 'العربية')}
            </div>

            <div className="menu-item" 
              onClick={() => {
                setIsDrawerOpen(false);
                if (user) {
                  setShowSettingsModal(true);
                } else {
                  setShowLoginModal(true);
                }
              }}
            >
              <FontAwesomeIcon icon={faCog} />
              {getLocalizedText('الإعدادات', 'Settings')}
            </div>

            <div className="menu-item" 
              onClick={() => {
                setIsDrawerOpen(false);
                setShowContactUsModal(true);
              }}
            >
              <FontAwesomeIcon icon={faEnvelope} />
              {getLocalizedText('تواصل معنا', 'Contact Us')}
            </div>

            {/* داخل الـ menu-items */}
            <div
              className="menu-item post-ad"
              onClick={() => {
                setIsDrawerOpen(false);
                if (user) {
                  setShowPostTypeModal(true);        // ← التعديل الجديد
                } else {
                  localStorage.setItem('redirectAfterLogin', '/post-ad');
                  setShowLoginModal(true);
                }
              }}
            >
              {getLocalizedText("أنشئ منشورك", "Create Your Post")}
            </div>

            {user ? (
              <div className="menu-item logout" onClick={() => setShowLogoutConfirm(true)}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                {getLocalizedText('تسجيل الخروج', 'Logout')}
              </div>
            ) : (
              <div className="menu-item login" onClick={() => { setIsDrawerOpen(false); setShowLoginModal(true); }}>
                <FontAwesomeIcon icon={faUser} />
                {getLocalizedText('تسجيل الدخول', 'Login')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ====================== Feature Modal ====================== */}
      {showFeatureModal && (
        <FeatureModal
          isOpen={showFeatureModal}
          onClose={() => {
            setShowFeatureModal(false);
            setFeatureType(null);
          }}
          type={featureType}
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          onSelect={(selectedItem) => {
            console.log('Selected Feature from mobile:', selectedItem);
            if (selectedItem.lat && selectedItem.lng) {
              updateSelectedLocation({
                lat: selectedItem.lat,
                lng: selectedItem.lng,
                address: getLocalizedText(selectedItem.ar, selectedItem.en)
              });
              setSearchQuery(getLocalizedText(selectedItem.ar, selectedItem.en));
            }
          }}
        />
      )}

      {/* Modals Section */}
      {showMapModal && (
        <MapModal
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          onClose={() => setShowMapModal(false)}
          onSelectLocation={(location) => {
            updateSelectedLocation(location);
            setSearchQuery(location.address || '');
          }}
        />
      )}

      {showLoginModal && (
        <LoginModal
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          onClose={(next) => {
            setShowLoginModal(false);
            if (next === 'forgot') setShowForgotPasswordModal(true);
            else if (next === 'register') setShowRegisterModal(true);
          }}
        />
      )}

      {showForgotPasswordModal && (
        <ForgotPasswordModal
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          onClose={() => {
            setShowForgotPasswordModal(false);
            setShowLoginModal(true);
          }}
        />
      )}

      {showRegisterModal && (
        <RegisterModal
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          onClose={(next) => {
            setShowRegisterModal(false);
            if (next === 'login') setShowLoginModal(true);
          }}
        />
      )}

      {showLogoutConfirm && (
        <LogoutConfirmModal
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          onConfirm={confirmLogout}
          onCancel={cancelLogout}
        />
      )}

      {showSettingsModal && (
        <SettingsModal
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          onClose={() => setShowSettingsModal(false)}
          initialSection="changeName"
        />
      )}

      {showContactUsModal && (
        <ContactUsModal
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          onClose={() => setShowContactUsModal(false)}
        />
      )}

      {/* Post Type Modal */}
     {showPostTypeModal && (
  <PostTypeModal
    currentLanguage={currentLanguage}
    getLocalizedText={getLocalizedText}
    onClose={() => setShowPostTypeModal(false)}
    onSelectRoom={() => {
      setShowPostTypeModal(false);
      window.location.href = '/post-ad';
    }}
    onSelectProfile={() => {
      setShowPostTypeModal(false);
      window.location.href = '/post-teamup';   // ← كان يوجه إلى post-profile[cite: 2]
    }}
  />
)}
    </>
  );
}

export default MobileHeader;